import React, { useState, useEffect, useRef } from 'react';
import { useRecovery } from '../../../features/recovery/useRecovery';
import '../../../styles/dossier.css';
import styles from './RecoveryAdmin.module.css';
import TerminalLoader from '../../../components/feedback/TerminalLoader';
import InlineTacticalLoader from '../../../components/feedback/InlineTacticalLoader';
import Typewriter from '../../../components/feedback/Typewriter';

/**
 * Helper: Splits bold markdown **text** into <strong> elements
 */
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className={styles.boldHighlight}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

/**
 * SentinelMessageContent - Renders structured AI responses
 */
function SentinelMessageContent({ content, isNew }) {
  const [showFull, setShowFull] = useState(!isNew);

  if (!showFull) {
    return (
      <Typewriter 
        text={content} 
        speed={15} 
        onComplete={() => setShowFull(true)} 
      />
    );
  }

  // Pre-process content: If it looks like a giant block but contains numbered instructions, 
  // try to inject newlines for the parser.
  const processedContent = content
    .replace(/(\d+\.)\s+\*\*/g, '\n$1 **') // Split before numbered items
    .replace(/(\*\*[A-Z\s]+:\*\*)/g, '\n$1'); // Split before bold headers like **SITUATION REPORT:**

  const lines = processedContent.split('\n');
  const elements = [];

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    // Headings: ### text
    if (trimmed.startsWith('### ')) {
      elements.push(<h4 key={idx} className={styles.msgHeading}>{renderInline(trimmed.slice(4))}</h4>);
      return;
    }

    // Bullet points: 1. or * or -
    if (/^(\d+\.|\*|-)\s+/.test(trimmed)) {
      elements.push(
        <li key={idx} className={styles.msgBullet}>
          {renderInline(trimmed.replace(/^(\d+\.|\*|-)\s+/, ''))}
        </li>
      );
      return;
    }

    // Paragraphs
    elements.push(<p key={idx} className={styles.msgPara}>{renderInline(trimmed)}</p>);
  });

  return <div className={styles.structuredResponse}>{elements}</div>;
}

const SobrietyClock = ({ lastReset }) => {
  const [elapsed, setElapsed] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    if (!lastReset) return;

    const calculate = () => {
      const start = new Date(lastReset);
      const now = new Date();
      const diff = Math.max(0, now - start);

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setElapsed({ days, hours, minutes, seconds });
    };

    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [lastReset]);

  const getStatusColor = () => {
    if (!lastReset) return 'rgba(36, 28, 22, 0.35)';
    if (elapsed.days < 7) return '#c23b2c'; // Critical
    if (elapsed.days < 30) return '#b8862e'; // Warning
    if (elapsed.days < 90) return '#5b7386'; // Stable
    return '#5c7a4f'; // Optimal
  };

  const color = getStatusColor();

  return (
    <div className={styles.clockContainer}>
      <div className={styles.clockTime} style={{ color, textShadow: `0 0 20px ${color}33` }}>
        <div className={styles.clockUnit}>
          <span className={styles.clockValue}>{elapsed.days}</span>
          <span className={styles.clockLabel}>Days</span>
        </div>
        <div className={styles.clockSeparator}>:</div>
        <div className={styles.clockUnit}>
          <span className={styles.clockValue}>{String(elapsed.hours).padStart(2, '0')}</span>
          <span className={styles.clockLabel}>Hrs</span>
        </div>
        <div className={styles.clockSeparator}>:</div>
        <div className={styles.clockUnit}>
          <span className={styles.clockValue}>{String(elapsed.minutes).padStart(2, '0')}</span>
          <span className={styles.clockLabel}>Min</span>
        </div>
        <div className={styles.clockSeparator}>:</div>
        <div className={styles.clockUnit}>
          <span className={styles.clockValue}>{String(elapsed.seconds).padStart(2, '0')}</span>
          <span className={styles.clockLabel}>Sec</span>
        </div>
      </div>
      <div className={styles.statusIndicator}>
        <div className={styles.statusDot} style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }} />
        <span className={styles.statusText}>SYSTEM_STATUS: {elapsed.days < 7 ? 'CRITICAL_VULNERABILITY' : 'NOMINAL_STABILITY'}</span>
      </div>
    </div>
  );
};

const RecoveryAdmin = () => {
  const {
    status,
    isLoading,
    isPlaceholderData,
    logUrge,
    isLoggingUrge,
    resetStreak,
    isResetting,
    panic,
    isPanicking,
    addReason,
    removeReason,
    chat,
    isChatting,
    generateBriefing,
    isGeneratingBriefing,
    surgicalReset,
    isSurgicallyResetting,
    testEmail,
    isTestingEmail
  } = useRecovery();

  const [urgeForm, setUrgeForm] = useState({ intensity: 5, trigger_context: '', notes: '' });
  const [newReason, setNewReason] = useState('');
  const [redirection, setRedirection] = useState(null);
  
  // Master Reset Handler
  const handleMasterReset = async () => {
    if (window.confirm('CRITICAL_AUTHORIZATION_REQUIRED: Are you sure you want to perform a full system wipe of recovery data? This will delete all logs, reasons, and briefings.')) {
      if (window.confirm('FINAL_CONFIRMATION: This action is irreversible. All test data will be purged.')) {
        try {
          await surgicalReset();
          setChatHistory([{ role: 'ai', content: 'SYSTEM_WIPE_COMPLETE: All recovery telemetry and configuration has been reset. System is now at Day 0.', isNew: true }]);
        } catch (err) {
          const errMsg = err.response?.data?.error || err.message || "Unknown Error";
          alert(`Failed to perform system reset.\n\nERROR_DETAILS: ${errMsg}`);
        }
      }
    }
  };

  const handleTestEmail = async () => {
    try {
      await testEmail();
      alert('Test request sent. Check your inbox and Render logs.');
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || "Unknown Error";
      alert(`Email test failed.\n\nERROR_DETAILS: ${errMsg}`);
    }
  };
  
  // UX State: Control the initial full-screen terminal loader
  const [hasInitialized, setHasInitialized] = useState(false);

  // Chat state
  const chatEndRef = useRef(null);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([
    { role: 'ai', content: 'Recovery_Sentinel v1.0 online. Private tactical channel secured. How can I assist with your recovery architecture today?', isNew: false }
  ]);

  // Handle loader completion
  const handleLoaderComplete = () => {
    setHasInitialized(true);
  };

  // Auto-scroll effect
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isChatting]);

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isChatting) return;

    const userMsg = { role: 'user', content: chatInput, isNew: false };
    setChatHistory(prev => [...prev, userMsg]);
    setChatInput('');

    try {
      const res = await chat({ message: chatInput, history: chatHistory.map(({role, content}) => ({role, content})) });
      setChatHistory(prev => [...prev, { role: 'ai', content: res.data.response, isNew: true }]);
    } catch (err) {
      console.error("AI Chat failed:", err);
      const errMsg = err.response?.data?.error || err.message || "Unknown Error";
      setChatHistory(prev => [...prev, { 
        role: 'ai', 
        content: `SYSTEM ERROR: Failed to reach AI Agent.\n\nERROR_DETAILS: ${errMsg}\n\nMaintain core stability.`, 
        isNew: true 
      }]);
    }
  };

  const handleLogUrge = async (e) => {
    e.preventDefault();
    if (isLoggingUrge) return;

    // Capture state values before clearing form
    const intensity = urgeForm.intensity;
    const context = urgeForm.trigger_context;
    const notes = urgeForm.notes;

    try {
      // Step 1: Commit the telemetry to DB
      await logUrge({ intensity, trigger_context: context, notes });
      
      // Step 2: Add visual confirmation to chat
      const actionMsg = `[TELEMETRY_LOGGED] Intensity: LVL_${intensity} | Context: ${context || 'N/A'}`;
      setChatHistory(prev => [...prev, { role: 'user', content: actionMsg, isNew: false }]);
      
      // Clear form immediately for UX
      setUrgeForm({ intensity: 5, trigger_context: '', notes: '' });

      // Step 3: Trigger AI Analysis
      const analysisPrompt = `SYSTEM_EVENT: User logged an urge. 
      INTENSITY: ${intensity}/10
      CONTEXT: ${context || 'Not specified'}
      NOTES: ${notes || 'None provided'}
      
      Provide a brief tactical analysis and one immediate stabilization protocol based on the behavioral notes provided.`;
      
      try {
        const res = await chat({ 
          message: analysisPrompt, 
          history: chatHistory.map(({role, content}) => ({role, content})) 
        });
        setChatHistory(prev => [...prev, { role: 'ai', content: res.data.response, isNew: true }]);
      } catch (aiErr) {
        console.error("AI Analysis failed:", aiErr);
        setChatHistory(prev => [...prev, { role: 'ai', content: 'SYSTEM WARNING: AI analysis offline, but telemetry was committed. Stay focused on your reasons for quitting.', isNew: true }]);
      }

    } catch (err) {
      console.error("Telemetry commit failed:", err);
      // More specific error message including server response if available
      const errMsg = err.response?.data?.error || err.message || "Unknown Error";
      setChatHistory(prev => [...prev, { 
        role: 'ai', 
        content: `CRITICAL: Database sync failure. Telemetry was NOT saved.\n\nERROR_DETAILS: ${errMsg}\n\nCheck network connection and maintain primary stability.`, 
        isNew: true 
      }]);
    }
  };

  const handlePanic = async () => {
    setRedirection('ANALYZING THREAT... INITIALIZING REDIRECTION PROTOCOL...');
    try {
      const res = await panic();
      setRedirection(res.data.redirection);
    } catch (err) {
      setRedirection('CONNECTION ERROR. FOCUS ON BREATH. YOU ARE IN CONTROL.');
    }
  };

  const handleReset = async () => {
    if (window.confirm('PROTOCOL WARNING: Are you sure you want to reset the streak? This will log a relapse.')) {
      const notes = window.prompt('Provide a post-incident report (optional):');
      await resetStreak({ notes });
    }
  };

  // Improved stability logic:
  // Only show TerminalLoader if we haven't initialized AND (loading or no status)
  if (!hasInitialized && (isLoading || !status)) {
    return <TerminalLoader onComplete={handleLoaderComplete} />;
  }

  return (
    <div className={`${styles.container} ${isPlaceholderData ? styles.revalidating : ''}`}>
      <div className={styles.scanline} />
      
      <header className={styles.header}>
        <div className={styles.titleWrapper}>
          <div className={styles.brand}>
            <span className={styles.brandIcon}>R</span>
            <h1 className={styles.title}>Recovery_Expert_System</h1>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.version}>v1.0.4_STABLE</span>
            <span className={styles.divider}>|</span>
            <span className={styles.sessionInfo}>SECURE_SESSION: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
        
        {/* DIAGNOSTIC PANEL */}
        <div className={styles.diagnosticPanel}>
          <div className={styles.diagnosticItem}>
            <span className={styles.diagLabel}>DB_INTERFACE:</span>
            <span className={status?.db_status === 'ONLINE' ? styles.statusOnline : styles.statusOffline}>
              {status?.db_status || 'UNKNOWN'}
            </span>
          </div>
          {status?.db_status === 'OFFLINE' && (
            <div className={styles.diagnosticError}>
              ERROR_LOG: {status.db_error}
            </div>
          )}
        </div>
      </header>

      <div className={styles.grid}>
        {/* SOBRIETY CLOCK */}
        <div className={`${styles.card} ${styles.clockCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.cardIndicator}></span>
            <h3>CORE_UPTIME_TELEMETRY</h3>
          </div>
          <SobrietyClock lastReset={status?.streak?.last_reset_at} />
          <div className={styles.panicActions}>
            <button 
              className={styles.panicButton} 
              onClick={handlePanic}
              disabled={isPanicking}
            >
              {isPanicking ? 'EXECUTING REDIRECTION...' : 'OVERRIDE PROTOCOL (PANIC)'}
            </button>
          </div>
          
          {redirection && (
            <div className={styles.redirection}>
              <div className={styles.redirectionHeader}>
                <div className={styles.redirectionTitleRow}>
                  <span className={styles.alertIcon}>!</span>
                  <strong>VIRTUAL_OPERATIVE_AI REDIRECTION</strong>
                </div>
                <button onClick={() => setRedirection(null)} className={styles.closeBtnIcon}>×</button>
              </div>
              <div className={styles.redirectionBody}>
                {isPanicking ? (
                  <InlineTacticalLoader message="ANALYZING THREAT VECTORS..." />
                ) : (
                  <>
                    <div className={styles.redirectionText}>
                      <SentinelMessageContent content={redirection} isNew={false} />
                    </div>
                    <div className={styles.redirectionFooter}>
                      <button onClick={() => setRedirection(null)} className={styles.acknowledgeBtn}>
                        ACKNOWLEDGE & CLOSE PROTOCOL
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* DAILY TACTICAL BRIEFING */}
        <div className={`${styles.card} ${styles.briefingCard}`}>
          <div className={styles.cardHeader}>
            <div className={styles.cardHeaderLeft}>
              <span className={styles.cardIndicator} style={{backgroundColor: '#b39a68'}}></span>
              <h3>DAILY_TACTICAL_BRIEFING</h3>
            </div>
            <div className={styles.briefingActions}>
              {status?.latest_briefing && (
                <span className={styles.briefingDate}>
                  {new Date(status.latest_briefing.sent_at).toLocaleDateString()}
                </span>
              )}
              <button 
                className={styles.syncBtn} 
                onClick={() => generateBriefing()}
                disabled={isGeneratingBriefing}
              >
                {isGeneratingBriefing ? 'SYNCING...' : 'MANUAL_SYNC'}
              </button>
            </div>
          </div>
          <div className={styles.briefingContent}>
            {status?.latest_briefing ? (
              <SentinelMessageContent content={status.latest_briefing.content} isNew={false} />
            ) : (
              <div className={styles.noBriefing}>
                <p>INITIALIZING DAILY BRIEFING PROTOCOL...</p>
                <p className={styles.subText}>System will generate first briefing at 08:00 HRS.</p>
              </div>
            )}
          </div>
        </div>

        {/* AI RECOVERY SENTINEL CHAT */}
        <div className={`${styles.card} ${styles.chatCard}`}>
          <div className={styles.cardHeader}>
            <div className={styles.cardHeaderLeft}>
              <span className={styles.cardIndicator} style={{backgroundColor: '#b39a68'}}></span>
              <h3>SENTINEL_AI (TACTICAL_ADVISOR)</h3>
            </div>
            <span className={styles.chatStatus}>ENCRYPTED_LINK_ACTIVE</span>
          </div>
          <div className={styles.chatMessages}>
            {chatHistory.map((msg, i) => (
              <div key={i} className={`${styles.message} ${msg.role === 'ai' ? styles.aiMessage : styles.userMessage}`}>
                <div className={styles.messageLabel}>{msg.role === 'ai' ? 'SENTINEL' : 'OPERATIVE'}</div>
                <div className={styles.messageContent}>
                  {msg.role === 'ai' ? (
                    <SentinelMessageContent content={msg.content} isNew={msg.isNew} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            {isChatting && <InlineTacticalLoader />}
            <div ref={chatEndRef} />
          </div>
          <form className={styles.chatInputArea} onSubmit={handleChat}>
            <div className={styles.inputWrapper}>
              <span className={styles.inputPrompt}>&gt;</span>
              <input 
                type="text" 
                className={styles.chatInput} 
                placeholder="Query specialized recovery advisor..." 
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                disabled={isChatting}
              />
            </div>
            <button type="submit" className={styles.chatSendBtn} disabled={isChatting}>
              EXEC
            </button>
          </form>
        </div>

        {/* TRIGGER TELEMETRY */}
        <div className={`${styles.card} ${styles.telemetryCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.cardIndicator} style={{backgroundColor: '#8a5a2b'}}></span>
            <h3>TRIGGER_TELEMETRY_LOG</h3>
          </div>
          <form onSubmit={handleLogUrge} className={styles.telemetryForm}>
            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>Urge Intensity</label>
                <span className={styles.intensityValue}>LVL_{urgeForm.intensity}</span>
              </div>
              <input 
                type="range" min="1" max="10" 
                className={styles.rangeInput} 
                value={urgeForm.intensity}
                onChange={(e) => setUrgeForm({...urgeForm, intensity: parseInt(e.target.value)})}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Context / Environment</label>
              <input 
                type="text" className={styles.input} placeholder="e.g. Stress at work, Home alone" 
                value={urgeForm.trigger_context}
                onChange={(e) => setUrgeForm({...urgeForm, trigger_context: e.target.value})}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Behavioral Notes</label>
              <textarea 
                className={styles.textarea} rows="2"
                placeholder="Briefly describe thoughts or triggers..."
                value={urgeForm.notes}
                onChange={(e) => setUrgeForm({...urgeForm, notes: e.target.value})}
              />
            </div>
            <button type="submit" className={styles.commitBtn} disabled={isLoggingUrge}>
              {isLoggingUrge ? 'COMMITTING...' : 'COMMIT_TELEMETRY'}
            </button>
          </form>
        </div>

        {/* CORE MOTIVATION (REASONS) */}
        <div className={`${styles.card} ${styles.objectivesCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.cardIndicator} style={{backgroundColor: '#5b7386'}}></span>
            <h3>STRATEGIC_OBJECTIVES</h3>
          </div>
          <div className={styles.reasonsContainer}>
            <ul className={styles.reasonsList}>
              {status?.reasons?.map(reason => (
                <li key={reason.id} className={styles.reasonItem}>
                  <span className={styles.reasonText}>{reason.content}</span>
                  <button className={styles.deleteBtn} onClick={() => removeReason(reason.id)}>DEL</button>
                </li>
              ))}
            </ul>
            <div className={styles.addReasonBox}>
              <input 
                type="text" className={styles.input} placeholder="Add new objective..." 
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (addReason({content: newReason}), setNewReason(''))}
              />
            </div>
          </div>
        </div>

        {/* RECENT ACTIVITY */}
        <div className={`${styles.card} ${styles.recentLogsCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.cardIndicator}></span>
            <h3>RECENT_INCIDENT_LOGS</h3>
          </div>
          <div className={styles.logs}>
            {status?.recent_logs?.map(log => (
              <div key={log.id} className={styles.logEntry}>
                <div className={styles.logHeader}>
                  <span className={styles.logType}>[{log.type}]</span>
                  <span className={styles.logMeta}>{new Date(log.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} {" // "} {new Date(log.created_at).toLocaleDateString()}</span>
                </div>
                <div className={styles.logBody}>
                  {log.intensity && <span className={styles.logIntensity}>LVL_{log.intensity}</span>}
                  {log.trigger_context && <span className={styles.logContext}>@{log.trigger_context}</span>}
                  {log.notes && <p className={styles.logNotes}>&quot;{log.notes}&quot;</p>}
                </div>
              </div>
            ))}
            {(!status?.recent_logs || status.recent_logs.length === 0) && (
              <div className={styles.emptyLogs}>NO_RECENT_TELEMETRY_AVAILABLE</div>
            )}
          </div>
        </div>

        {/* SYSTEM RESET */}
        <div className={`${styles.card} ${styles.resetCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.cardIndicator} style={{backgroundColor: '#c23b2c'}}></span>
            <h3>FAILURE_PROCEDURE</h3>
          </div>
          <p className={styles.resetWarning}>
            WARNING: Resetting the streak logs a RELAPSE incident. Use only after a complete failure of primary systems.
          </p>
          <div className={styles.resetActions}>
            <button className={styles.resetButton} onClick={handleReset} disabled={isResetting}>
              {isResetting ? 'EXECUTING RESET...' : 'INITIATE_SYSTEM_RESET'}
            </button>
            <button className={styles.testEmailBtn} onClick={handleTestEmail} disabled={isTestingEmail}>
              {isTestingEmail ? 'SENDING_TEST...' : 'TEST_EMAIL_SYSTEM'}
            </button>
            <button className={styles.masterResetBtn} onClick={handleMasterReset} disabled={isSurgicallyResetting}>
              {isSurgicallyResetting ? 'PURGING_DATA...' : 'MASTER_SYSTEM_WIPE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecoveryAdmin;
